import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { faAngleLeft, faBookmark as bookmarkIcon } from '@fortawesome/pro-regular-svg-icons';
import { faCartShoppingFast, faConveyorBeltAlt } from '@fortawesome/pro-solid-svg-icons';
import { Store } from '@ngrx/store';
import { CustomerFeature } from 'app/customer-integration/customer-integration';
import { Subscription } from 'rxjs';
import { tryToBulkHoldSelectedItems } from '../../../bulk-select/actions/bulk-select.actions';
import { BulkFeatureKey } from '../../../bulk-select/enums/bulk-feature-key';
import { SitePermissions } from '../../../permissions/models/permissions';
import { moveBookmarks } from '../../actions/list.actions';
import {
  BookmarksSearchType,
  ListItem,
  ListItemEntity,
  ListWithItemsCount,
  SearchAvailabilityFilter
} from '../../models/list';
import { BookmarkSearch } from '../searchable-bookmarks-search-bar/searchable-bookmarks-search-bar.component';
import { ListSearchService } from 'app/list/services/list-search.service';

@Component({
  selector: 'app-searchable-bookmarks-available-list',
  templateUrl: './searchable-bookmarks-available-list.component.html',
  styleUrls: ['./searchable-bookmarks-available-list.component.scss']
})
export class SearchableBookmarksAvailableListComponent implements OnInit, OnDestroy {
  @Input() public lists: ListWithItemsCount[];
  @Input() public authorized: boolean;
  @Output() public closeList = new EventEmitter();
  public itemsEntitySelected: ListItemEntity[];
  public itemsSelectedCount: number = 0;
  public itemsSelectedCountSearch: number = 0;
  public isAllSelected: boolean = false;
  public isAllSelectedSearch: boolean = false;

  // icons
  public showcaseIcon = faConveyorBeltAlt;
  public placeHoldsIcon = faCartShoppingFast;
  public readonly arrowIconLeft = faAngleLeft;
  public readonly bookmarkIcon = bookmarkIcon;

  // view data - search
  public items: ListItem[] = [];
  public itemsSearch: ListItem[] = [];
  public itemsDisplay: ListItem[] = [];
  public itemsSearchDisplay: ListItem[] = [];
  public loading: boolean = true;
  public latestSearch: BookmarkSearch;
  public isSearch = false;
  public availableCount: number;
  public availableCountSearch: number;
  public page: number = 0;

  // constants
  public sitePermissions = SitePermissions;
  public readonly bulkFeatureKey = BulkFeatureKey.BOOKMARKS;
  public readonly CustomerFeature = CustomerFeature;
  public availabilityFilter: SearchAvailabilityFilter = SearchAvailabilityFilter.Available;

  private readonly subscriptions: Subscription = new Subscription();

  constructor(
    private readonly store: Store,
    private readonly listSearchService: ListSearchService,
  ) {
  }

  ngOnInit() {
    const availableSearch = {text: '', availableOnly: true, type: BookmarksSearchType.Author};
    this.onSearch(availableSearch, true);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  public async onSearch(search: BookmarkSearch, initial = false) {
    this.page = 0;
    if (search.text || initial) {
      this.isSearch = true;
      this.isAllSelectedSearch = false;
    } else {
      this.isSearch = false;
      this.isAllSelected = false;
      this.loading = false;
      return;
    }
    this.loading = true;
    this.listSearchService.searchAll(search, initial).subscribe(({available}) => {
      this.items = available;
      this.itemsSearch = this.items.filter(i => i.matched);
      this.itemsDisplay = this.items.slice(this.page, this.page + 100);
      this.itemsSearchDisplay = this.itemsSearch.slice(this.page, this.page + 100);
      this.page++;
      this.updateSelected();
      this.loading = false;
    });
  }

  public loadMoreItems() {
    const start = this.page * 100;
    this.itemsDisplay.push(...this.items.slice(start, start + 100));
    this.itemsSearchDisplay.push(...this.itemsSearch.slice(start, start + 100));
    this.page++;
  }

  public setAllItemsSelection(listId: string, selected: boolean): void {
    this.isAllSelected = selected;
    this.items.forEach(item => item.selected = this.isAllSelected);
    this.updateSelected();
  }

  public setAllItemsSelectionSearch(listId: string, selected: boolean): void {
    this.isAllSelectedSearch = selected;
    this.itemsSearch.forEach(item => {
      if (item.matched) {
        item.selected = this.isAllSelectedSearch;
      }
    });
    this.updateSelected();
  }

  public updateSelected() {
    this.itemsEntitySelected = this.items.filter((item) => item.selected).map((item) => item.entity);
    this.itemsSelectedCount = this.itemsEntitySelected.length;
    const selectedMatches = this.itemsSearch.filter((item) => item.matched && item.selected);
    this.itemsSelectedCountSearch = selectedMatches.length;
    this.availableCount = this.items.length;
    this.availableCountSearch = this.itemsSearch.length;
  }

  public onToggleItem(listId: string, listItem: ListItem): void {
    const item = this.items.find(item => item.id === listItem.id);
    if (item) {
      item.selected = !item.selected;
    }
    this.updateSelected();
  }

  public removeSelectedFromList(): void {
    const items = this.itemsEntitySelected.map((item) => item);
    this.store.dispatch(moveBookmarks({entities: items, fromListIds: ['available-list']}));
  }

  public bulkPlaceHolds(event: Event) {
    event.stopPropagation();
    if (this.itemsEntitySelected.length) {
      const items = this.itemsEntitySelected.map((item) => item.sourceEntity);
      this.store.dispatch(tryToBulkHoldSelectedItems({bulkFeatureKey: this.bulkFeatureKey, items}));
    }
  }
}
